// Pieces the job page's watch and breach tabs share.

import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatTimestamp } from '../lib/format';
import { envRegionLabel } from '../lib/rollbackProfiles';
import { tierColor } from '../lib/rollbackWatches';
import { getColors } from '../theme/tokens';
import { TIER_HELP } from '../copy/rollbackProfileHelp';

export const mono = {
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: '0.8rem',
};

/** Shown in display time; storage and the API are UTC, so the tooltip gives that. */
export const UtcTime: React.FC<{ value?: string }> = ({ value }) =>
  value ? (
    <Tooltip title={value} arrow placement="top" enterDelay={200}>
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {formatTimestamp(value)}
      </Box>
    </Tooltip>
  ) : (
    <>—</>
  );

export const TierChip: React.FC<{ tier?: string | null }> = ({ tier }) =>
  tier ? (
    <Tooltip title={TIER_HELP[tier] || ''} arrow placement="top" enterDelay={200}>
      <Chip label={tier} size="small" color={tierColor(tier)} sx={{ height: 22, fontSize: '0.72rem' }} />
    </Tooltip>
  ) : (
    <>—</>
  );

export const EnvRegionFilter: React.FC<{
  choices: string[];
  selected: string;
  onSelect: (envRegion: string) => void;
}> = ({ choices, selected, onSelect }) => (
  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
    {['', ...choices].map(e => (
      <Chip
        key={e || 'all'}
        label={e ? envRegionLabel(e) : 'All'}
        size="small"
        clickable
        color={e === selected ? 'primary' : 'default'}
        variant={e === selected ? 'filled' : 'outlined'}
        onClick={() => onSelect(e)}
      />
    ))}
  </Box>
);

export const ListLoading: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: c.inkMuted }}>
      <CircularProgress size={14} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
};

export const LoadOlder: React.FC<{ loading: boolean; onClick: () => void }> = ({ loading, onClick }) => (
  <Box sx={{ mt: 1.5 }}>
    <Button
      size="small"
      variant="outlined"
      onClick={onClick}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={12} /> : undefined}
    >
      Load older
    </Button>
  </Box>
);

export const ListError: React.FC<{ error?: string }> = ({ error }) =>
  error ? (
    <Alert severity="error" sx={{ mb: 2 }}>
      {error}
    </Alert>
  ) : null;
